import { useState } from "react";
import { Loader2, Power, AlertCircle } from "lucide-react";
import { useEc2InstanceStatus } from "./useEc2InstanceStatus";
import { ConfirmModal } from "@/shared/components/common";

export function InstanceStatusIndicator() {
  const {
    isLoading,
    isError,
    isRunning,
    isTransitioning,
    isPending,
    isTerminated,
    isStarting,
    isStopping,
    startInstance,
    stopInstance,
    refetchStatus,
  } = useEc2InstanceStatus();

  const [confirmStopOpen, setConfirmStopOpen] = useState(false);
  const [confirmStartOpen, setConfirmStartOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs font-medium border border-gray-200 dark:border-gray-700">
        <Loader2 className="w-3 h-3 animate-spin text-gray-500" />
        <span>Checking Instance...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <button
        type="button"
        onClick={refetchStatus}
        className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 text-xs font-medium border border-red-200 dark:border-red-800 hover:bg-red-100 transition-colors cursor-pointer"
        title="Click to retry"
      >
        <AlertCircle className="w-3 h-3" />
        <span>Instance Status Error (Retry)</span>
      </button>
    );
  }

  if (isStarting || isPending) {
    return (
      <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 text-xs font-medium border border-amber-200 dark:border-amber-800">
        <Loader2 className="w-3 h-3 animate-spin text-amber-600" />
        <span>Starting Instance...</span>
      </div>
    );
  }

  if (isStopping || isTransitioning) {
    return (
      <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 text-xs font-medium border border-amber-200 dark:border-amber-800">
        <Loader2 className="w-3 h-3 animate-spin text-amber-600" />
        <span>Stopping Instance...</span>
      </div>
    );
  }

  if (isRunning) {
    return (
      <>
        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 text-xs font-medium border border-emerald-200 dark:border-emerald-800">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          <span>Instance Running</span>
          <button
            type="button"
            onClick={() => setConfirmStopOpen(true)}
            className="ml-1 text-[10px] text-red-600 dark:text-red-400 hover:underline cursor-pointer font-medium"
          >
            Stop
          </button>
        </div>

        <ConfirmModal
          isOpen={confirmStopOpen}
          title="Stop EC2 Instance?"
          message="Stopping the instance will halt background ingestion jobs until it is restarted."
          confirmLabel="Stop Instance"
          cancelLabel="Cancel"
          confirmVariant="danger"
          onConfirm={() => {
            setConfirmStopOpen(false);
            stopInstance();
          }}
          onCancel={() => setConfirmStopOpen(false)}
        />
      </>
    );
  }

  // A terminated instance cannot be started again, so no Start control.
  if (isTerminated) {
    return (
      <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs font-medium border border-gray-200 dark:border-gray-700">
        <span className="h-2 w-2 rounded-full bg-gray-500 shrink-0" />
        <span>Instance Terminated</span>
      </div>
    );
  }

  // Stopped state
  return (
    <>
      <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-400 text-xs font-medium border border-rose-200 dark:border-rose-800">
        <span className="h-2 w-2 rounded-full bg-rose-500 shrink-0" />
        <span>Instance Stopped</span>
        <button
          type="button"
          onClick={() => setConfirmStartOpen(true)}
          className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700 transition-colors cursor-pointer"
        >
          <Power className="w-2.5 h-2.5" />
          <span>Start</span>
        </button>
      </div>

      <ConfirmModal
        isOpen={confirmStartOpen}
        title="Start EC2 Instance?"
        message="Starting the EC2 instance allows course document analysis and structure generation."
        confirmLabel="Start Instance"
        cancelLabel="Cancel"
        confirmVariant="primary"
        onConfirm={() => {
          setConfirmStartOpen(false);
          startInstance();
        }}
        onCancel={() => setConfirmStartOpen(false)}
      />
    </>
  );
}
